/*
  Maison Milan — scrollable rails, family filtering, testimonial slides.

  Custom elements rather than inline script, so the theme editor can tear a
  section down and rebuild it without leaking listeners, and a section added
  in the editor works without a page reload.
*/

/*
  Wrapped.

  Nine sections load this file, and a page that uses two of them evaluates it
  twice. A top-level `class` is a lexical declaration in global scope, so the
  second evaluation threw "Identifier 'MaisonRail' has already been declared"
  — at parse time, which discards the entire second copy before a single line
  of it runs. The guards at the foot of the file never got the chance to do
  their job.

  Nothing was visibly broken, because the first copy had already registered
  every element. But it put a SyntaxError in the console of every product and
  collection page, and it meant the guards were load-bearing in name only.
  Function scope makes the redeclaration legal and lets the guards work.
*/
(() => {

/*
  Shared rail behaviour: a horizontally scrolling track with prev/next
  controls. MaisonFilter extends it and adds the family chips, so the
  best-sellers row and the filterable collection behave identically.
*/
class MaisonRail extends HTMLElement {
  connectedCallback() {
    this.prev = this.querySelector('[data-rail-prev]');
    this.next = this.querySelector('[data-rail-next]');
    this.dots = this.querySelector('[data-rail-dots]');
    this.viewall = this.querySelector('[data-rail-viewall]');
    this.panels = [...this.querySelectorAll('[data-rail-panel]')];
    this.tabs = [...this.querySelectorAll('[data-rail-tab]')];

    /* One set of arrows serves every tab, so the controller has to know which
     * track it is currently driving rather than binding one at construction.
     * Without this the arrows kept paging the first tab's row after the
     * reader had switched to the third. */
    this.bindTrack(this.querySelector('[data-rail-panel]:not([hidden]) [data-rail-track]') ||
                   this.querySelector('[data-rail-track]'));
    if (!this.track) return;

    this.addEventListener('click', this.onRailClick);
    window.addEventListener('resize', this.onResize);
    this.buildDots();
    this.syncNav();
  }

  bindTrack(track) {
    if (this.track === track) return;
    if (this.track) {
      this.track.removeEventListener('wheel', this.onWheel);
      this.track.removeEventListener('scroll', this.syncNav);
    }
    this.track = track || null;
    if (!this.track) return;
    this.track.addEventListener('wheel', this.onWheel, { passive: false });
    this.track.addEventListener('scroll', this.syncNav, { passive: true });
  }

  showTab(index) {
    if (!this.panels.length) return;
    this.tabs.forEach((t, i) => {
      const on = i === index;
      t.classList.toggle('is-on', on);
      t.setAttribute('aria-selected', String(on));
    });
    this.panels.forEach((p, i) => { p.hidden = i !== index; p.classList.toggle('is-on', i === index); });

    /* "View all" has to follow the tab, or it sends a reader who is looking
     * at one collection to a different one. */
    const url = this.tabs[index] && this.tabs[index].dataset.tabUrl;
    if (this.viewall && url) this.viewall.href = url;

    /* A hidden track has no width, so its dots and arrow states could not be
     * computed until now — they are built after the panel is shown. */
    this.bindTrack(this.panels[index].querySelector('[data-rail-track]'));
    if (this.track) this.track.scrollLeft = 0;
    this.buildDots();
    this.syncNav();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onRailClick);
    if (!this.track) return;
    this.track.removeEventListener('wheel', this.onWheel);
    this.track.removeEventListener('scroll', this.syncNav);
    window.removeEventListener('resize', this.onResize);
  }

  onRailClick = (event) => {
    const tab = event.target.closest('[data-rail-tab]');
    if (tab) return this.showTab(Number(tab.dataset.railTab));
    if (event.target.closest('[data-rail-prev]')) return this.page(-1);
    if (event.target.closest('[data-rail-next]')) return this.page(1);
    const dot = event.target.closest('[data-rail-dot]');
    if (dot) return this.goTo(Number(dot.dataset.railDot));
  };

  onResize = () => {
    this.buildDots();
    this.syncNav();
  };

  /*
    Dots are built in JS because the page count depends on rendered width,
    which Liquid cannot know. One dot per viewport of track; none at all when
    the row does not overflow, so a short filter result shows no orphan dot.
  */
  buildDots() {
    if (!this.dots || !this.track) return;
    const pages = this.pageCount();
    if (pages < 2) {
      this.dots.innerHTML = '';
      this.dots.hidden = true;
      return;
    }
    this.dots.hidden = false;
    this.dots.innerHTML = Array.from({ length: pages }, (_, i) =>
      `<button type="button" class="mm-rail__dot" data-rail-dot="${i}" ` +
      `aria-label="Page ${i + 1} of ${pages}"></button>`
    ).join('');
  }

  pageCount() {
    if (!this.track || this.track.clientWidth === 0) return 0;
    return Math.ceil(this.track.scrollWidth / this.track.clientWidth);
  }

  goTo(index) {
    if (!this.track) return;
    this.track.scrollTo({ left: index * this.track.clientWidth, behavior: 'auto' });
    this.syncNav();
  }

  /*
    A horizontal scroll container swallows vertical wheel deltas, so with the
    pointer over the row the page cannot be scrolled past it. Forward vertical
    intent to the window and leave genuine horizontal intent (trackpad swipe,
    shift-wheel) to the browser.
  */
  onWheel = (event) => {
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;
    event.preventDefault();
    window.scrollBy(0, event.deltaY);
  };

  /*
    Scroll by most of a viewport, keeping a card's overlap as a visual anchor.

    behavior:'auto' rather than 'smooth', and no CSS scroll-behavior on the
    track either. Both were tested: smooth never animated, and adding CSS
    scroll-behavior:smooth cut an 'auto' scroll from 548px to 3px — the
    animation starts and is immediately cancelled. Instant paging is the only
    variant that reliably moves.
  */
  page(direction) {
    if (!this.track) return;

    /* At the end of a category the arrows carry on into the next one instead
     * of dead-ending, and wrap round from the last back to the first. So the
     * three rows read as one continuous run: keep pressing next and you walk
     * Best selling, Oud & woods, Floral, Best selling, for ever.
     *
     * Only when there are tabs to walk. A single-collection rail still stops
     * at its ends, because there is nowhere else for it to go. */
    if (this.tabs && this.tabs.length > 1) {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      const atEnd = x >= max - 2;
      const atStart = x <= 2;
      const current = this.panels.findIndex((p) => !p.hidden);

      if (direction > 0 && atEnd) {
        const nextTab = (current + 1) % this.tabs.length;
        this.showTab(nextTab);
        if (this.track) this.track.scrollLeft = 0;
        return;
      }
      if (direction < 0 && atStart) {
        const prevTab = (current - 1 + this.tabs.length) % this.tabs.length;
        this.showTab(prevTab);
        /* Arriving backwards means arriving at the end of that row, not the
         * start — otherwise stepping back and forth lands you somewhere you
         * have not been. */
        if (this.track) this.track.scrollLeft = this.track.scrollWidth;
        this.syncNav();
        return;
      }
    }

    // A full viewport per step, so the arrows land exactly where the dots
    // point. An overlapping step would leave the two disagreeing.
    const step = this.track.clientWidth;
    this.track.scrollBy({ left: step * direction, behavior: 'auto' });
    // Sync straight away rather than waiting on a scroll event: in testing the
    // track moved 822px without firing one, leaving the arrows stuck. The
    // listener still covers swipe and drag, which have no explicit call site.
    this.syncNav();
  }

  syncNav = () => {
    if (!this.track) return;
    const max = this.track.scrollWidth - this.track.clientWidth;
    const x = this.track.scrollLeft;
    /* 2px tolerance: sub-pixel layout leaves scrollLeft a hair short of max.
     * With tabs the arrows are never disabled — at the end of a row they step
     * into the next category, so greying them out would be a lie. */
    const loops = this.tabs && this.tabs.length > 1;
    if (this.prev) this.prev.disabled = loops ? false : x <= 2;
    if (this.next) this.next.disabled = loops ? false : x >= max - 2;

    if (!this.dots || this.dots.hidden) return;
    const all = this.dots.querySelectorAll('[data-rail-dot]');
    // The final page never scrolls a full viewport — max is short of
    // (pages - 1) * clientWidth — so rounding alone never reaches the last
    // dot. At the end of the track, the last dot is the active one.
    const current =
      x >= max - 2
        ? all.length - 1
        : Math.round(x / Math.max(this.track.clientWidth, 1));
    all.forEach((dot, i) => {
      const active = i === current;
      dot.setAttribute('aria-current', active ? 'true' : 'false');
      dot.classList.toggle('is-active', active);
    });
  };
}

class MaisonFilter extends MaisonRail {
  connectedCallback() {
    super.connectedCallback();
    this.chips = Array.from(this.querySelectorAll('[data-family-filter]'));
    this.items = Array.from(this.querySelectorAll('[data-family]'));
    this.empty = this.querySelector('[data-filter-empty]');
    this.status = this.querySelector('[data-filter-status]');
    this.addEventListener('click', this.onFilterClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.onFilterClick);
  }

  onFilterClick = (event) => {
    const chip = event.target.closest('[data-family-filter]');
    if (!chip || !this.contains(chip)) return;
    this.apply(chip.dataset.familyFilter);
  };

  apply(value) {
    let shown = 0;

    this.chips.forEach((chip) => {
      chip.setAttribute('aria-pressed', String(chip.dataset.familyFilter === value));
    });

    this.items.forEach((item) => {
      const match = value === 'all' || item.dataset.family === value;
      item.hidden = !match;
      if (match) shown += 1;
    });

    if (this.empty) this.empty.hidden = shown !== 0;
    if (this.track) this.track.scrollTo({ left: 0, behavior: 'auto' });
    // Hidden items change the track width, so the dot count changes with it.
    this.buildDots();
    this.syncNav();

    // Announce the result rather than leaving a screen reader to discover
    // that the grid silently changed underneath it.
    if (this.status) {
      this.status.textContent =
        shown === 0
          ? 'No fragrances in this family.'
          : `${shown} fragrance${shown === 1 ? '' : 's'} shown.`;
    }
  }
}

class MaisonQuotes extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-quote-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-quote-dot]'));
    if (this.slides.length < 2) return;
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  onClick = (event) => {
    const dot = event.target.closest('[data-quote-dot]');
    if (!dot || !this.contains(dot)) return;
    this.show(Number(dot.dataset.quoteDot));
  };

  onKeydown = (event) => {
    if (!event.target.closest('[data-quote-dot]')) return;
    const current = this.dots.findIndex((d) => d.getAttribute('aria-selected') === 'true');
    let next = null;
    if (event.key === 'ArrowRight') next = (current + 1) % this.dots.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + this.dots.length) % this.dots.length;
    if (next === null) return;
    event.preventDefault();
    this.show(next);
    this.dots[next].focus();
  };

  show(index) {
    this.slides.forEach((slide, i) => { slide.hidden = i !== index; });
    this.dots.forEach((dot, i) => {
      dot.setAttribute('aria-selected', String(i === index));
      dot.setAttribute('tabindex', i === index ? '0' : '-1');
    });
  }
}

/*
  Quantity stepper. Self-contained so a card works anywhere — the home rails,
  the collection grid, or any section that renders maison-card.
*/
class MaisonQty extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.addEventListener('click', this.onClick);
    this.input.addEventListener('change', this.clamp);
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    if (this.input) this.input.removeEventListener('change', this.clamp);
  }

  onClick = (event) => {
    const btn = event.target.closest('button');
    if (!btn) return;
    // The stepper sits inside the add-to-cart form; without this the browser
    // treats these as submit buttons and adds to the bag on every tap.
    event.preventDefault();
    this.step(btn.dataset.qty === 'up' ? 1 : -1);
  };

  step(delta) {
    const min = Number(this.input.min || 1);
    const next = Math.max(min, (Number(this.input.value) || min) + delta);
    this.input.value = next;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.sync();
  }

  clamp = () => {
    const min = Number(this.input.min || 1);
    if (!Number.isFinite(Number(this.input.value)) || Number(this.input.value) < min) {
      this.input.value = min;
    }
    this.sync();
  };

  sync() {
    const min = Number(this.input.min || 1);
    const down = this.querySelector('[data-qty="down"]');
    if (down) down.disabled = Number(this.input.value) <= min;
  }
}

if (!customElements.get('maison-qty')) customElements.define('maison-qty', MaisonQty);
if (!customElements.get('maison-rail')) customElements.define('maison-rail', MaisonRail);
if (!customElements.get('maison-filter')) customElements.define('maison-filter', MaisonFilter);
if (!customElements.get('maison-quotes')) customElements.define('maison-quotes', MaisonQuotes);
})();
